import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Card, Container, Form } from "react-bootstrap";
import CommonButton from "../../../components/commonButton";
import CustomSelect from "../../../components/form/CustomSelect";
import InputField from "../../../components/form/InputField";
import { ArrowLeftIcon } from "../../../assets/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useMounted from "../../../hooks/useMounted";
import { Formik } from "formik";
import * as Yup from "yup";
import { useLoader } from "../../../context/LoaderContext";
import { useToast } from "../../../context/TostContext";
import { FormFeedback, FormLabel } from "../../../components/form/utils";
import { goBack } from "../../../utils/otherHelopers";

import MapPickerModal from "../../../components/MapPickerModal";
import ScrollToFieldError from "../../../components/ScrollToFieldError";
import PreviewModal from "../../../components/modals/previewModal";

import {
  fetchPropertyConfig,
  fetchBrokerClients,
  uploadPropertyFile,
  deletePropertyFile,
  addBrokerProperty,
  updateBrokerProperty,
  addBrokerPublicProperty,
  updateBrokerPublicProperty,
  fetchDropdownUsers,
} from "../../../redux/slice/allPropertiesSlice";

import {
  fetchActiveStates,
  fetchActiveCities,
  fetchActiveDistricts,
  fetchActiveTalukas,
  fetchActiveVillages,
  fetchActiveLocalities,
} from "../../../redux/slice/locationSlice";
import { MapPin } from "lucide-react";


const FormikPriceCalculator = ({ values, setFieldValue }) => {
  useEffect(() => {
    if (values.property_type) {
      const isLand = values.property_type === "Land";
      const area = parseFloat(isLand ? values.land_area : values.s_builtup_area) || 0;
      const rate = parseFloat(values.price_per_unit) || 0;
      const total = area * rate;
      const calculatedStr = total > 0 ? String(total) : "";
      if (values.total_price !== calculatedStr) {
        setFieldValue("total_price", calculatedStr);
      }
    }
  }, [values.s_builtup_area, values.land_area, values.price_per_unit, values.property_type, values.total_price, setFieldValue]);

  return null;
};


const AddBrokerProperty = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const hasMounted = useMounted();
  const { setLoading } = useLoader();
  const {id} = useParams();
  const editValues = location?.state?.item;
  const isEdit = location?.state?.isEdit;
  const isPublicProperty = location?.state?.isPublicProperty || (isEdit && editValues?.property_id === null);
  const fromUserDetail = location?.state?.fromUserDetail;
  const headerPortalTarget = document.getElementById("header-portal-target");
  const backUrl = fromUserDetail
    ? `/user/user-detail/${editValues?.user_id || editValues?.user?.id || location?.state?.item?.user_id}?tab=${location?.state?.isLand ? 'public-lands' : 'public-properties'}`
    : (location?.state?.fromGlobalPublic
      ? (location?.state?.isLand ? "/public-lands" : "/public-properties")
      : "/broker-crm/properties");
  const toast = useToast();
  const [showMapModal, setShowMapModal] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null); // { type: 'image' | 'video' | 'document', url, title }

  // Selectors
  const propertyConfig = useSelector((state) => state.allProperties?.propertyConfig);
  const { states = [], cities = [], districts = [], talukas = [], villages = [], localities = [] } = useSelector((state) => state.location || {});
  const { brokerClients = [] } = useSelector((state) => state.allProperties || {});

  // Local state for broker users (fetchDropdownUsers result is not stored in Redux)
  const [dropdownUsers, setDropdownUsers] = useState([]);
  const [propertyType, setPropertyType] = useState(editValues?.property_type || (location?.state?.isLand ? "Land" : ""));

  // Ref so search handlers can always read the latest Formik values without closures
  const valuesRef = useRef({});

  // One search-debounce ref per dropdown
  const brokerSearchRef = useRef(null);
  const clientSearchRef = useRef(null);
  const stateSearchRef = useRef(null);
  const districtSearchRef = useRef(null);
  const talukaSearchRef = useRef(null);
  const villageSearchRef = useRef(null);
  const citySearchRef = useRef(null);
  const localitySearchRef = useRef(null);

  const [selectedOptions, setSelectedOptions] = useState({
    user: null,
    client: null,
    state: null,
    district: null,
    taluka: null,
    village: null,
    city: null,
    locality: null,
  });

  useEffect(() => {
    if (editValues) {
      setSelectedOptions({
        user: editValues.user ? {
          value: editValues.user.id,
          label: `${editValues.user.first_name || ""} ${editValues.user.last_name || ""} (${editValues.user.mobile_number || ""})`.trim() || "Unknown User"
        } : null,
        client: editValues.client ? {
          value: editValues.client.id,
          label: `${editValues.client.name} (${editValues.client.mobile_number})`
        } : null,
        state: editValues.state ? { value: editValues.state.id, label: editValues.state.name } : null,
        district: editValues.district ? { value: editValues.district.id, label: editValues.district.name } : null,
        taluka: editValues.taluka ? { value: editValues.taluka.id, label: editValues.taluka.name } : null,
        village: editValues.village ? { value: editValues.village.id, label: editValues.village.name } : null,
        city: editValues.city ? { value: editValues.city.id, label: editValues.city.name } : null,
        locality: editValues.locality ? { value: editValues.locality.id, label: editValues.locality.name } : null,
      });
      setPropertyType(editValues.property_type || (location?.state?.isLand ? "Land" : ""));
    }
  }, [editValues, location]);

  // ─── Option lists (EditProfileModal pattern) ──────────────────────────────
  const brokerOptions = useMemo(() => {
    const fetched = dropdownUsers.map(u => ({
      value: u.id,
      label: `${u.first_name || ""} ${u.last_name || ""} (${u.mobile_number || ""})`.trim() || "Unknown User",
    }));
    const cur = selectedOptions.user || (editValues?.user ? {
      value: editValues.user.id,
      label: `${editValues.user.first_name || ""} ${editValues.user.last_name || ""} (${editValues.user.mobile_number || ""})`.trim() || "Unknown User"
    } : null);
    if (cur && cur.value && !fetched.find(o => o.value === cur.value)) {
      fetched.unshift(cur);
    }
    return fetched;
  }, [dropdownUsers, editValues, selectedOptions.user]);

  const clientOptions = useMemo(() => {
    const fetched = brokerClients.map(c => ({ value: c.id, label: `${c.name} (${c.mobile_number})` }));
    const cur = selectedOptions.client || (editValues?.client ? {
      value: editValues.client.id,
      label: `${editValues.client.name} (${editValues.client.mobile_number})`
    } : null);
    if (cur && cur.value && !fetched.find(o => o.value === cur.value)) {
      fetched.unshift(cur);
    }
    return fetched;
  }, [brokerClients, editValues, selectedOptions.client]);

  const stateOptions = useMemo(() => {
    const fetched = states.map(s => ({ value: s.id, label: s.name }));
    const cur = selectedOptions.state || (editValues?.state ? { value: editValues.state.id, label: editValues.state.name } : null);
    if (cur && cur.value && !fetched.find(o => o.value === cur.value)) {
      fetched.unshift(cur);
    }
    return fetched;
  }, [states, editValues, selectedOptions.state]);

  const districtOptions = useMemo(() => {
    const fetched = districts.map(d => ({ value: d.id, label: d.name }));
    const cur = selectedOptions.district || (editValues?.district ? { value: editValues.district.id, label: editValues.district.name } : null);
    if (cur && cur.value && !fetched.find(o => o.value === cur.value)) {
      fetched.unshift(cur);
    }
    return fetched;
  }, [districts, editValues, selectedOptions.district]);

  const talukaOptions = useMemo(() => {
    const fetched = talukas.map(t => ({ value: t.id, label: t.name }));
    const cur = selectedOptions.taluka || (editValues?.taluka ? { value: editValues.taluka.id, label: editValues.taluka.name } : null);
    if (cur && cur.value && !fetched.find(o => o.value === cur.value)) {
      fetched.unshift(cur);
    }
    return fetched;
  }, [talukas, editValues, selectedOptions.taluka]);

  const villageOptions = useMemo(() => {
    const fetched = villages.map(v => ({ value: v.id, label: v.name }));
    const cur = selectedOptions.village || (editValues?.village ? { value: editValues.village.id, label: editValues.village.name } : null);
    if (cur && cur.value && !fetched.find(o => o.value === cur.value)) {
      fetched.unshift(cur);
    }
    return fetched;
  }, [villages, editValues, selectedOptions.village]);

  const cityOptions = useMemo(() => {
    const fetched = cities.map(c => ({ value: c.id, label: c.name }));
    const cur = selectedOptions.city || (editValues?.city ? { value: editValues.city.id, label: editValues.city.name } : null);
    if (cur && cur.value && !fetched.find(o => o.value === cur.value)) {
      fetched.unshift(cur);
    }
    return fetched;
  }, [cities, editValues, selectedOptions.city]);

  const localityOptions = useMemo(() => {
    const fetched = localities.map(l => ({ value: l.id, label: l.name }));
    const cur = selectedOptions.locality || (editValues?.locality ? { value: editValues.locality.id, label: editValues.locality.name } : null);
    if (cur && cur.value && !fetched.find(o => o.value === cur.value)) {
      fetched.unshift(cur);
    }
    return fetched;
  }, [localities, editValues, selectedOptions.locality]);



  // ─── Search handlers ──────────────────────────────────────────────────────
  const handleBrokerSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (brokerSearchRef.current) clearTimeout(brokerSearchRef.current);
      brokerSearchRef.current = setTimeout(() => {
        dispatch(fetchDropdownUsers({ search: inputValue, limit: 50 })).unwrap()
          .then(res => setDropdownUsers(res?.data?.data || res?.data || []))
          .catch(() => { });
      }, 400);
    }
  };

  const handleClientSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (clientSearchRef.current) clearTimeout(clientSearchRef.current);
      clientSearchRef.current = setTimeout(() => {
        if (valuesRef.current?.user_id)
          dispatch(fetchBrokerClients({ user_id: valuesRef.current.user_id, search: inputValue }));
      }, 400);
    }
  };

  const handleStateSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (stateSearchRef.current) clearTimeout(stateSearchRef.current);
      stateSearchRef.current = setTimeout(() => dispatch(fetchActiveStates({ search: inputValue, limit: 50 })), 400);
    }
  };

  const handleDistrictSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (districtSearchRef.current) clearTimeout(districtSearchRef.current);
      districtSearchRef.current = setTimeout(() =>
        dispatch(fetchActiveDistricts({ search: inputValue, limit: 50, state_ids: valuesRef.current?.state_id })), 400);
    }
  };

  const handleTalukaSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (talukaSearchRef.current) clearTimeout(talukaSearchRef.current);
      talukaSearchRef.current = setTimeout(() =>
        dispatch(fetchActiveTalukas({ search: inputValue, limit: 50, district_ids: valuesRef.current?.district_id })), 400);
    }
  };

  const handleVillageSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (villageSearchRef.current) clearTimeout(villageSearchRef.current);
      villageSearchRef.current = setTimeout(() =>
        dispatch(fetchActiveVillages({ search: inputValue, limit: 50, taluka_ids: valuesRef.current?.taluka_id })), 400);
    }
  };

  const handleCitySearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (citySearchRef.current) clearTimeout(citySearchRef.current);
      citySearchRef.current = setTimeout(() =>
        dispatch(fetchActiveCities({ search: inputValue, limit: 50, state_ids: valuesRef.current?.state_id })), 400);
    }
  };

  const handleLocalitySearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (localitySearchRef.current) clearTimeout(localitySearchRef.current);
      localitySearchRef.current = setTimeout(() =>
        dispatch(fetchActiveLocalities({ search: inputValue, limit: 50, city_ids: valuesRef.current?.city_id })), 400);
    }
  };



  // Initial Values
  const initialEventState = useMemo(() => {
    return {
      user_id: editValues?.user_id || "",
      client_id: editValues?.client_id || "",
      client_type: editValues?.client_type || "",
      transaction_type: editValues?.transaction_type || "",
      property_type: editValues?.property_type || (location?.state?.isLand ? "Land" : ""),
      land_type: editValues?.land_type || "",
      tenure: editValues?.tenure || "",
      state_id: editValues?.state_id || "",
      district_id: editValues?.district_id || "",
      taluka_id: editValues?.taluka_id || "",
      village_id: editValues?.village_id || "",
      city_id: editValues?.city_id || "",
      locality_id: editValues?.locality_id || "",
      bhk: editValues?.bhk || "",
      carpet_area: editValues?.carpet_area || "",
      carpet_area_unit: editValues?.carpet_area_unit || "Sqft",
      s_builtup_area: editValues?.s_builtup_area || "",
      s_builtup_area_unit: editValues?.s_builtup_area_unit || "Sqft",
      plot_area: editValues?.plot_area || "",
      plot_area_unit: editValues?.plot_area_unit || "Sqft",
      possession: editValues?.possession || "",
      furnishing_status: editValues?.furnishing_status || "",
      society_project_name: editValues?.society_project_name || "",
      developer_name: editValues?.developer_name || "",
      land_area: editValues?.land_area || "",
      land_area_unit: editValues?.land_area_unit || "Sqft",
      survey_no: editValues?.survey_no || "",
      fp_no: editValues?.fp_no || "",
      tp_no: editValues?.tp_no || "",
      zone: editValues?.zone || "",
      road_type: editValues?.road_type || "",
      road_size: editValues?.road_size || "",
      jantri_rate: editValues?.jantri_rate || "",
      dastaveg_value: editValues?.dastaveg_value || "",
      tag: editValues?.tag || "",
      price_per_unit: editValues?.price_per_unit || "",
      price_unit: editValues?.price_unit || "Sqft",
      total_price: editValues?.total_price || "",
      map_location: {
        address: editValues?.map_location?.address || "",
        latitude: editValues?.map_location?.latitude || "",
        longitude: editValues?.map_location?.longitude || "",
      },
      amenities: editValues?.amenities || [],
      notes: editValues?.notes || "",
      bath: editValues?.bath || "",
      lift: editValues?.lift || "",
      parking_car: editValues?.parking_car || "",
      parking_2w: editValues?.parking_2w || "",
      floor_no: editValues?.floor_no || "",
      flat_per_floor: editValues?.flat_per_floor || "",
      total_floor: editValues?.total_floor || "",
      rent_earning: editValues ? (editValues.rent_earning === true ? "Yes" : (editValues.rent_earning === false ? "No" : "")) : "",
      tenant_type: editValues?.tenant_type || "",
      rent_per_month: editValues?.rent_per_month || "",
      rent_per_unit: editValues?.rent_per_unit || "",
      deposit: editValues?.deposit || "",
      contract_tenure: editValues?.contract_tenure || "",
      contract_duration: editValues?.contract_duration || "",
      rent_increment: editValues?.rent_increment || "",
      rent_increment_tenure: editValues?.rent_increment_tenure || "",
      lock_in: editValues?.lock_in || "",
      notice_period: editValues?.notice_period || "",
      maintenance_amount_per_month: editValues?.maintenance_amount_per_month || "",
      municipal_tax_annually: editValues?.municipal_tax_annually || "",
      marjo_property_type: editValues?.marjo_property_type || "",
      marjo_city_id: editValues?.marjo_city_id || "",
      marjo_locality_id: editValues?.marjo_locality_id || "",
      marjo_price: editValues?.marjo_price || "",
      gst: editValues?.gst || "",
      marjo_note: editValues?.marjo_note || "",
      is_public: isPublicProperty ? true : (editValues?.is_public ?? false),
      photos: editValues?.photos || [],
      video: editValues?.video || null,
      brochure_attachment: editValues?.brochure_attachment || null,
    };
  }, [editValues, isPublicProperty, location]);

  // Fetch initial configs, broker list and state list
  useEffect(() => {
    dispatch(fetchPropertyConfig());
    dispatch(fetchActiveStates({ limit: 50 }));
    dispatch(fetchDropdownUsers({ limit: 50 })).unwrap()
      .then(res => setDropdownUsers(res?.data?.data || res?.data || []))
      .catch(() => { });
  }, [dispatch]);

  // Validation Schema Generator
  const getValidationSchema = (propertyType) => {
    const isLand = propertyType === "Land";
    const hasPropertyType = !!propertyType;

    // plot_area is visible when propertyType in Row House, Residential Plot, Industrial Plot, Industrial Shed/ Building
    const showPlotArea = hasPropertyType && [
      "Row House",
      "Residential Plot",
      "Industrial Plot",
      "Industrial Shed/ Building",
    ].includes(propertyType);

    // furnishing_status is visible when propertyType is NOT Land and NOT in Residential Plot, Industrial Plot, Industrial Shed/ Building
    const showFurnishing = hasPropertyType && !isLand && ![
      "Residential Plot",
      "Industrial Plot",
      "Industrial Shed/ Building",
    ].includes(propertyType);

    const showBhk = hasPropertyType && [
      "Flat",
      "Penthouse",
      "Serviced Apartment",
      "Row House",
    ].includes(propertyType);

    return Yup.object().shape({
      user_id: Yup.string().required("Broker selection is required"),
      client_type: isPublicProperty
        ? Yup.string().nullable()
        : Yup.string().required("Client type is required"),
      client_id: isPublicProperty
        ? Yup.string().nullable()
        : Yup.string().required("Client is required"),
      transaction_type: Yup.string().required("Transaction type is required"),
      property_type: Yup.string().required("Property type is required"),
      state_id: Yup.string().required("State is required"),
      bhk: showBhk
        ? Yup.string().required("BHK is required")
        : Yup.string().nullable(),
      land_type: isLand
        ? Yup.string().required("Land type is required")
        : Yup.string().nullable(),
      tenure: isLand
        ? Yup.string().required("Tenure is required")
        : Yup.string().nullable(),

      district_id: isLand
        ? Yup.string().required("District is required")
        : Yup.string().nullable(),
      taluka_id: isLand
        ? Yup.string().required("Taluka is required")
        : Yup.string().nullable(),
      city_id: !isLand
        ? Yup.string().required("City is required")
        : Yup.string().nullable(),

      carpet_area: Yup.number()
        .transform((value, originalValue) => (originalValue === "" ? null : value))
        .positive("Must be positive")
        .nullable(),
      s_builtup_area: !isLand && hasPropertyType
        ? Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .positive("Must be positive")
          .required("Super builtup area is required")
        : Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .positive("Must be positive")
          .nullable(),
      plot_area: showPlotArea
        ? Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .positive("Must be positive")
          .required("Plot area is required")
        : Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .positive("Must be positive")
          .nullable(),
      land_area: isLand
        ? Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .positive("Must be positive")
          .required("Land area is required")
        : Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .positive("Must be positive")
          .nullable(),
      price_per_unit: hasPropertyType
        ? Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .positive("Must be positive")
          .required("Price per unit is required")
        : Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .positive("Must be positive")
          .nullable(),
      total_price: hasPropertyType
        ? Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .positive("Must be positive")
          .required("Total price is required")
        : Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .positive("Must be positive")
          .nullable(),
      possession: !isLand && hasPropertyType
        ? Yup.string().required("Possession is required")
        : Yup.string().nullable(),
      furnishing_status: showFurnishing
        ? Yup.string().required("Furnishing status is required")
        : Yup.string().nullable(),
      map_location: Yup.object().shape({
        address: Yup.string().nullable()
          .test('addr-required', 'Address is required', function (value) {
            const { latitude, longitude } = this.parent || {};
            const hasLat = latitude !== undefined && latitude !== null && latitude !== "";
            const hasLng = longitude !== undefined && longitude !== null && longitude !== "";
            if (hasLat || hasLng) {
              return !!value?.trim();
            }
            return true;
          }),
        latitude: Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .typeError("Must be a number")
          .nullable()
          .test('lat-required', 'Latitude is required', function (value) {
            const { address, longitude } = this.parent || {};
            const hasAddress = !!address?.trim();
            const hasLng = longitude !== undefined && longitude !== null && longitude !== "";
            if (hasAddress || hasLng) {
              return value !== undefined && value !== null && value !== "";
            }
            return true;
          }),
        longitude: Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .typeError("Must be a number")
          .nullable()
          .test('lng-required', 'Longitude is required', function (value) {
            const { address, latitude } = this.parent || {};
            const hasAddress = !!address?.trim();
            const hasLat = latitude !== undefined && latitude !== null && latitude !== "";
            if (hasAddress || hasLat) {
              return value !== undefined && value !== null && value !== "";
            }
            return true;
          }),
      }).nullable(),
    });
  };


  // Handlers for dynamic dropdown state cascading
  const handleSearchableBrokerChange = (name, value, values, setValues) => {
    setValues({
      ...values,
      [name]: value,
      client_id: "",
    });
    if (value) {
      dispatch(fetchBrokerClients({ user_id: value }));
    }
  };


  const handleStateChange = (selectedOption, values, setValues, currentPropertyType) => {
    const stateId = selectedOption?.value || "";
    setValues({
      ...values,
      state_id: stateId,
      city_id: "",
      locality_id: "",
      district_id: "",
      taluka_id: "",
      village_id: "",
    });
    if (stateId) {
      if (currentPropertyType === "Land") {
        dispatch(fetchActiveDistricts({ state_ids: stateId, limit: 1000 }));
      } else {
        dispatch(fetchActiveCities({ state_ids: stateId, limit: 1000 }));
      }
    }
  };

  const handlePropertyTypeChange = (selectedOption, values, setValues, stateId) => {
    const propType = selectedOption?.value || "";
    setValues({
      ...values,
      property_type: propType,
      district_id: "",
      taluka_id: "",
      village_id: "",
      city_id: "",
      locality_id: "",
    });
    setPropertyType(propType);
    if (stateId) {
      if (propType === "Land") {
        dispatch(fetchActiveDistricts({ state_ids: stateId, limit: 1000 }));
      } else {
        dispatch(fetchActiveCities({ state_ids: stateId, limit: 1000 }));
      }
    }
  };

  const handleDistrictChange = (selectedOption, values, setValues) => {
    const districtId = selectedOption?.value || "";
    setValues({
      ...values,
      district_id: districtId,
      taluka_id: "",
      village_id: "",
    });
    if (districtId) {
      dispatch(fetchActiveTalukas({ district_ids: districtId, limit: 1000 }));
    }
  };

  const handleTalukaChange = (selectedOption, values, setValues) => {
    const talukaId = selectedOption?.value || "";
    setValues({
      ...values,
      taluka_id: talukaId,
      village_id: "",
    });
    if (talukaId) {
      dispatch(fetchActiveVillages({ taluka_ids: talukaId, limit: 1000 }));
    }
  };

  const handleCityChange = (selectedOption, values, setValues) => {
    const cityId = selectedOption?.value || "";
    setValues({
      ...values,
      city_id: cityId,
      locality_id: "",
    });
    if (cityId) {
      dispatch(fetchActiveLocalities({ city_ids: cityId, limit: 1000 }));
    }
  };

  // Upload Handlers
  const handleFileUpload = async (event, type, values, setFieldValue) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!values.user_id) {
      toast.error("Please select a broker first.");
      return;
    }

    setLoading(true);
    try {
      const result = await dispatch(uploadPropertyFile({ user_id: values.user_id, file })).unwrap();
      if (result?.success && result?.data?.length > 0) {
        const uploadedFile = result.data[0];
        if (type === "photos") {
          setFieldValue("photos", [...values.photos, uploadedFile]);
        } else if (type === "video") {
          setFieldValue("video", uploadedFile);
        } else if (type === "brochure_attachment") {
          setFieldValue("brochure_attachment", uploadedFile);
        }
        toast.success("File uploaded successfully.");
      } else {
        toast.error("Failed to upload file.");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to upload file.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileDelete = async (fileId, type, values, setFieldValue) => {
    if (!values.user_id) return;

    setLoading(true);
    try {
      await dispatch(deletePropertyFile({ id: fileId, user_id: values.user_id })).unwrap();
      if (type === "photos") {
        setFieldValue("photos", values.photos.filter((f) => f.id !== fileId));
      } else if (type === "video") {
        setFieldValue("video", null);
      } else if (type === "brochure_attachment") {
        setFieldValue("brochure_attachment", null);
      }
      toast.success("File deleted successfully.");
    } catch (error) {
      toast.error(error?.message || "Failed to delete file.");
    } finally {
      setLoading(false);
    }
  };

  const handleAmenityToggle = (amenity, values, setFieldValue) => {
    const current = values.amenities || [];
    if (current.includes(amenity)) {
      setFieldValue("amenities", current.filter((a) => a !== amenity));
    } else {
      setFieldValue("amenities", [...current, amenity]);
    }
  };

  // Submit Handler
  const handleSubmit = async (values, { setSubmitting }) => {
    const parseNum = (val) => {
      if (val === "" || val === undefined || val === null) return null;
      const num = Number(val);
      return isNaN(num) ? null : num;
    };

    const payload = {
      user_id: values.user_id,
      client_id: values.client_id,
      client_type: values.client_type,
      transaction_type: values.transaction_type,
      property_type: values.property_type,
      state_id: values.state_id,
      is_public: values.is_public,
      price_per_unit: parseNum(values.price_per_unit),
      price_unit: values.price_unit || null,
      total_price: parseNum(values.total_price),
      notes: values.notes || null,
      map_location: (values.map_location &&
        (values.map_location.address?.trim() ||
          (values.map_location.latitude !== "" && values.map_location.latitude !== null) ||
          (values.map_location.longitude !== "" && values.map_location.longitude !== null)))
        ? {
          address: values.map_location.address || "",
          latitude: parseNum(values.map_location.latitude),
          longitude: parseNum(values.map_location.longitude),
        }
        : null,
    };

    const isRentEarning = values.transaction_type === "Rent" || values.rent_earning === "Yes" || values.rent_earning === true;
    payload.rent_earning = isRentEarning;

    const rentalFields = [
      "tenant_type",
      "contract_tenure",
      "contract_duration",
      "rent_increment",
      "rent_increment_tenure",
      "lock_in",
      "notice_period"
    ];
    const rentalNumericFields = [
      "rent_per_month",
      "rent_per_unit",
      "deposit",
      "maintenance_amount_per_month",
      "municipal_tax_annually"
    ];

    rentalFields.forEach((field) => {
      payload[field] = isRentEarning ? (values[field] || null) : null;
    });
    rentalNumericFields.forEach((field) => {
      payload[field] = isRentEarning ? parseNum(values[field]) : null;
    });

    if (values.property_type === "Land") {
      // Set Land fields
      payload.district_id = values.district_id || null;
      payload.taluka_id = values.taluka_id || null;
      payload.village_id = values.village_id || null;
      payload.land_type = values.land_type || null;
      payload.tenure = values.tenure || null;
      payload.land_area = parseNum(values.land_area);
      payload.land_area_unit = values.land_area_unit || null;
      payload.survey_no = values.survey_no || null;
      payload.fp_no = values.fp_no || null;
      payload.tp_no = values.tp_no || null;
      payload.zone = values.zone || null;
      payload.road_type = values.road_type || null;
      payload.road_size = values.road_size || null;
      payload.jantri_rate = values.jantri_rate || null;
      payload.dastaveg_value = values.dastaveg_value || null;
      payload.tag = values.tag || null;

      // Nullify property fields
      const propertyFields = [
        "city_id", "locality_id", "bhk", "carpet_area", "carpet_area_unit",
        "s_builtup_area", "s_builtup_area_unit", "plot_area", "plot_area_unit",
        "possession", "furnishing_status", "society_project_name", "developer_name",
        "floor_no", "flat_per_floor", "total_floor", "no_of_wings", "bath", "lift",
        "parking_car", "parking_2w", "amenities"
      ];
      propertyFields.forEach((field) => {
        if (field === "amenities") {
          payload[field] = [];
        } else {
          payload[field] = null;
        }
      });
    } else {
      // Set Property fields
      payload.city_id = values.city_id || null;
      payload.locality_id = values.locality_id || null;
      payload.bhk = ["Flat", "Penthouse", "Serviced Apartment", "Row House"].includes(values.property_type)
        ? (values.bhk || null)
        : null;
      payload.carpet_area = parseNum(values.carpet_area);
      payload.carpet_area_unit = values.carpet_area_unit || null;
      payload.s_builtup_area = parseNum(values.s_builtup_area);
      payload.s_builtup_area_unit = values.s_builtup_area_unit || null;

      // plot_area only for specific types
      const plotAreaTypes = ["Row House", "Residential Plot", "Industrial Plot", "Industrial Shed/ Building"];
      if (plotAreaTypes.includes(values.property_type)) {
        payload.plot_area = parseNum(values.plot_area);
        payload.plot_area_unit = values.plot_area_unit || null;
      } else {
        payload.plot_area = null;
        payload.plot_area_unit = null;
      }

      payload.possession = values.possession || null;
      payload.furnishing_status = ["Residential Plot", "Industrial Plot", "Industrial Shed/ Building"].includes(values.property_type)
        ? null
        : (values.furnishing_status || null);
      payload.society_project_name = values.society_project_name || null;
      payload.developer_name = values.developer_name || null;
      const isFieldEnabled = (field) => {
        if (propertyConfig?.fields?.[values.property_type]) {
          return !!propertyConfig.fields[values.property_type][field];
        }
        const propType = values.property_type;
        if (["Residential Plot", "Industrial Plot", "Industrial Shed/ Building", "Land"].includes(propType)) {
          return false;
        }
        if (field === "flat_per_floor" || field === "no_of_wings") {
          if (["Office", "Shop - Showroom", "Row House"].includes(propType)) {
            return false;
          }
        }
        if (field === "floor_no") {
          if (propType === "Row House") {
            return false;
          }
        }
        return true;
      };

      payload.floor_no = isFieldEnabled("floor_no") ? (values.floor_no || null) : null;
      payload.flat_per_floor = isFieldEnabled("flat_per_floor") ? (values.flat_per_floor || null) : null;
      payload.total_floor = isFieldEnabled("total_floor") ? (values.total_floor || null) : null;
      payload.no_of_wings = isFieldEnabled("no_of_wings") ? (values.no_of_wings || null) : null;
      payload.bath = isFieldEnabled("bath") ? (values.bath || null) : null;
      payload.lift = isFieldEnabled("lift") ? (values.lift || null) : null;
      payload.parking_car = isFieldEnabled("parking_car") ? (values.parking_car || null) : null;
      payload.parking_2w = isFieldEnabled("parking_2w") ? (values.parking_2w || null) : null;
      payload.amenities = values.amenities || [];

      // Nullify land fields
      const landFields = [
        "district_id", "taluka_id", "village_id", "land_type", "tenure",
        "land_area", "land_area_unit", "survey_no", "fp_no", "tp_no",
        "zone", "road_type", "road_size", "jantri_rate", "dastaveg_value", "tag"
      ];
      landFields.forEach((field) => {
        payload[field] = null;
      });
    }

    // Set file payloads
    payload.photos = (values.photos || []).map((f) => ({ id: f.id }));
    payload.video = values.video ? { id: values.video.id } : null;
    payload.brochure_attachment = values.brochure_attachment ? { id: values.brochure_attachment.id } : null;

    // Nullify unused Marjo fields
    const marjoFields = [
      "marjo_property_type",
      "marjo_city_id",
      "marjo_locality_id",
      "marjo_price",
      "gst",
      "marjo_note"
    ];
    marjoFields.forEach((field) => {
      payload[field] = null;
    });

    setLoading(true);
    try {
      if (isPublicProperty) {
        // Public property flow - no client, direct to public_properties table
        delete payload.client_id;
        delete payload.client_type;
        payload.is_public = true;
        if (isEdit) {
          await dispatch(updateBrokerPublicProperty({ id, data: payload })).unwrap();
          toast.success("Public property updated successfully!");
        } else {
          await dispatch(addBrokerPublicProperty({ data: payload })).unwrap();
          toast.success("Public property added successfully!");
        }
      } else {
        // Regular property flow
        if (isEdit) {
          await dispatch(updateBrokerProperty({ id, data: payload })).unwrap();
          toast.success("Property updated successfully!");
        } else {
          await dispatch(addBrokerProperty({ data: payload })).unwrap();
          toast.success("Property added successfully!");
        }
      }
      setTimeout(() => {
        if (hasMounted) {
          goBack(navigate, location, backUrl);
        }
      }, 1000);
    } catch (err) {
      const errorMessage = err?.message || "Failed to save property details.";
      toast.error(errorMessage);
      setSubmitting(false);
      setLoading(false);
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <Container fluid className={`px-0`}>


      <Formik
        initialValues={initialEventState}
        validationSchema={getValidationSchema(propertyType)}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          errors,
          touched,
          values,
          setFieldValue,
          setValues,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => {
          valuesRef.current = values; // Keep ref in sync so search handlers can read latest values

          // Dynamic field builders
          const getNestedValue = (obj, path) => {
            if (!obj) return undefined;
            return path.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), obj);
          };

          const renderInput = (name, label, type = "text", placeholder = "", required = false, readOnly = false) => {
            const val = name.includes('.') ? (getNestedValue(values, name) || "") : (values[name] || "");
            const hasError = name.includes('.')
              ? (getNestedValue(errors, name) && getNestedValue(touched, name))
              : (errors[name] && touched[name]);
            const errorMsg = name.includes('.') ? getNestedValue(errors, name) : errors[name];

            return (
              <InputField
                className="g-col-6 g-col-xxl-4 mb-3"
                label={{ name: label }}
                required={required}
                input={{
                  id: name,
                  name: name,
                  type: type,
                  placeholder: placeholder || `Enter ${label.toLowerCase()}`,
                  value: val,
                  onChange: handleChange,
                  onBlur: handleBlur,
                  readOnly: readOnly,
                }}
                validation={{
                  isInvalid: hasError,
                  message: errorMsg,
                }}
              />
            );
          };

          const renderTextArea = (name, label, placeholder = "") => {
            return (
              <InputField
                className="g-col-12 mb-3"
                label={{ name: label }}
                input={{
                  id: name,
                  name: name,
                  as: "textarea",
                  rows: 3,
                  placeholder: placeholder || `Enter ${label.toLowerCase()}`,
                  value: values[name] || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                }}
                validation={{
                  isInvalid: touched[name] && errors[name],
                  message: errors[name],
                }}
              />
            );
          };

          const renderDropdown = (name, label, options, required = false, isClearable = true) => {
            return (
              <CustomSelect
                label={{ name: label }}
                name={name}
                value={values[name]}
                onChange={(opt) => setFieldValue(name, opt ? opt.value : "")}
                options={options}
                placeholder={`Select ${label.toLowerCase()}`}
                validation={{
                  isInvalid: touched[name] && errors[name],
                  message: errors[name],
                }}
                required={required}
                isClearable={isClearable}
                className="g-col-6 g-col-xxl-4 mb-3"
              />
            );
          };

          const renderAreaField = (name, label, unitName, required = false) => {
            let isVisible = false;
            if (name === "carpet_area" || name === "s_builtup_area") {
              isVisible = values.property_type && values.property_type !== "Land";
            } else if (name === "plot_area") {
              isVisible = values.property_type && [
                "Row House",
                "Residential Plot",
                "Industrial Plot",
                "Industrial Shed/ Building",
              ].includes(values.property_type);
            } else if (name === "land_area") {
              isVisible = values.property_type === "Land";
            }

            if (!isVisible) return null;
            return (
              <div className="g-col-6 g-col-xxl-4 mb-3">
                <FormLabel id={name} labelName={label} required={required} />
                <div className="d-flex gap-2">
                  <div className="flex-grow-1">
                    <InputField
                      label={{ isHidden: true }}
                      input={{
                        id: name,
                        name: name,
                        type: "text",
                        placeholder: `Enter ${label.toLowerCase()}`,
                        value: values[name] || "",
                        onChange: handleChange,
                        onBlur: handleBlur,
                      }}
                      validation={{
                        isInvalid: touched[name] && errors[name],
                        message: errors[name],
                      }}
                    />
                  </div>
                  <div style={{ width: "120px" }}>
                    <CustomSelect
                      name={unitName}
                      value={values[unitName] || "Sqft"}
                      onChange={(opt) => setFieldValue(unitName, opt ? opt.value : "Sqft")}
                      options={(propertyConfig?.unit_types || []).map((u) => ({ value: u, label: u }))}
                      isClearable={false}
                    />
                  </div>
                </div>
              </div>
            );
          };

          const renderPriceField = (name, label, unitName, required = false) => {
            return (
              <div className="g-col-6 g-col-xxl-4 mb-3">
                <FormLabel id={name} labelName={label} required={required} />
                <div className="d-flex gap-2">
                  <div className="flex-grow-1">
                    <InputField
                      label={{ isHidden: true }}
                      input={{
                        id: name,
                        name: name,
                        type: "text",
                        placeholder: `Enter ${label.toLowerCase()}`,
                        value: values[name] || "",
                        onChange: handleChange,
                        onBlur: handleBlur,
                      }}
                      validation={{
                        isInvalid: touched[name] && errors[name],
                        message: errors[name],
                      }}
                    />
                  </div>
                  <div style={{ width: "120px" }}>
                    <CustomSelect
                      name={unitName}
                      value={values[unitName] || "Sqft"}
                      onChange={(opt) => setFieldValue(unitName, opt ? opt.value : "Sqft")}
                      options={(propertyConfig?.unit_types || []).map((u) => ({ value: u, label: u }))}
                      isClearable={false}
                    />
                  </div>
                </div>
              </div>
            );
          };

          const renderMapLocationField = () => {
            return (
              <div className="g-col-6 g-col-xxl-4 mb-3">
                <FormLabel labelName="Map Location" />
                <div className="d-flex gap-2">
                  <div className="flex-grow-1">
                    <InputField
                      label={{ isHidden: true }}
                      input={{
                        type: "text",
                        placeholder: "Latitude",
                        value: values.map_location?.latitude || "",
                        readOnly: true,
                      }}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <InputField
                      label={{ isHidden: true }}
                      input={{
                        type: "text",
                        placeholder: "Longitude",
                        value: values.map_location?.longitude || "",
                        readOnly: true,
                      }}
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-outline-primary d-flex align-items-center gap-1 h-100 px-3"
                      onClick={() => setShowMapModal(true)}
                    >
                      <MapPin size={18} />
                      <span>Pick</span>
                    </button>
                  </div>
                </div>
                {((touched.map_location?.latitude && errors.map_location?.latitude) ||
                  (touched.map_location?.longitude && errors.map_location?.longitude)) && (
                    <FormFeedback
                      message={
                        (touched.map_location?.latitude && errors.map_location?.latitude) ||
                        (touched.map_location?.longitude && errors.map_location?.longitude)
                      }
                    />
                  )}
              </div>
            );
          };
          return (
            <Form onSubmit={handleSubmit}>
              <FormikPriceCalculator values={values} setFieldValue={setFieldValue} />
              <ScrollToFieldError />
              {headerPortalTarget && (
                createPortal(
                  <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                    <div className="d-flex align-items-center gap-2">
                      <button
                        onClick={() => { goBack(navigate, location, backUrl) }}
                        type="button"
                        className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
                      >
                        <ArrowLeftIcon size={22} />
                      </button>
                      <h4 className="fw-semibold mb-0 text-primary fs-lg">
                        {
                          isEdit
                            ? (isPublicProperty ? (propertyType === "Land" ? "Edit Public Land" : "Edit Public Property") : (propertyType === "Land" ? "Edit Broker Land" : "Edit Broker Property"))
                            : (isPublicProperty ? (propertyType === "Land" ? "New Public Land" : "New Public Property") : (propertyType === "Land" ? "New Broker Land" : "New Broker Property"))
                        }
                      </h4>
                    </div>
                  </div>,
                  headerPortalTarget
                )
              )}
              <Card className="bg-white border-0 p-4 rounded-2 shadow-sm">
                <div className="mt-4 pt-1">
                  {/* Basic Settings Card */}
                  <Card className="p-3 mb-4 border bg-light bg-opacity-10">
                    <h6 className="mb-3 text-muted">Basic Info</h6>
                    <div className="grid gap-3">

                      <CustomSelect
                        label={{ name: "Broker" }}
                        className="g-col-6 g-col-xxl-4 mb-3"
                        inputId="user_id"
                        required
                        options={brokerOptions}
                        value={brokerOptions.find(o => o.value === values.user_id) || null}
                        onChange={(opt) => {
                          handleSearchableBrokerChange("user_id", opt?.value || "", values, setValues);
                          setSelectedOptions(prev => ({ ...prev, user: opt, client: null }));
                        }}
                        onInputChange={handleBrokerSearch}
                        onMenuOpen={() => {
                          dispatch(fetchDropdownUsers({ limit: 50 })).unwrap()
                            .then(res => setDropdownUsers(res?.data?.data || res?.data || []))
                            .catch(() => { });
                        }}
                        placeholder="Select Broker"
                        isClearable={!fromUserDetail && !isEdit}
                        isDisabled={fromUserDetail || isEdit}
                        validation={{
                          isInvalid: touched.user_id && errors.user_id,
                          message: errors.user_id,
                        }}
                      />



                      {!isPublicProperty && (
                        <CustomSelect
                          label={{ name: "Client Type" }}
                          name="client_type"
                          value={values.client_type}
                          onChange={(opt) => setFieldValue("client_type", opt ? opt.value : "")}
                          options={(propertyConfig?.client_types || []).map((ct) => ({ value: ct, label: ct }))}
                          placeholder="Select Client Type"
                          validation={{
                            isInvalid: touched.client_type && errors.client_type,
                            message: errors.client_type,
                          }}
                          required={true}
                          className="g-col-6 g-col-xxl-4 mb-3"
                        />
                      )}

                      {!isPublicProperty && (

                        <CustomSelect
                          className="g-col-6 g-col-xxl-4 mb-3"
                          inputId="client_id"
                          options={clientOptions}
                          label={{ name: "Client" }}
                          required
                          value={clientOptions.find(o => o.value === values.client_id) || null}
                          onChange={(opt) => {
                            setFieldValue("client_id", opt?.value || "");
                            setSelectedOptions(prev => ({ ...prev, client: opt }));
                          }}
                          onInputChange={handleClientSearch}
                          onMenuOpen={() => {
                            if (values.user_id) {
                              dispatch(fetchBrokerClients({ user_id: values.user_id }));
                            }
                          }}
                          placeholder="Select Client"
                          isClearable
                          isDisabled={!values.user_id}

                          filterOption={() => true}
                          validation={{
                            isInvalid: touched.client_id && errors.client_id,
                            message: errors.client_id,
                          }}
                        />

                      )}

                      <CustomSelect
                        label={{ name: "Property Type" }}
                        name="property_type"
                        options={(() => {
                          const opts = (propertyConfig?.property_types || []).map((pt) => ({ value: pt, label: pt }));
                          if (location?.state?.isLand || values.property_type === "Land") {
                            if (!opts.find(o => o.value === "Land")) {
                              opts.push({ value: "Land", label: "Land" });
                            }
                          }
                          return opts;
                        })()}
                        value={values.property_type}
                        onChange={(opt) => handlePropertyTypeChange(opt, values, setValues, values.state_id)}
                        placeholder="Select Property Type"
                        validation={{
                          isInvalid: touched.property_type && errors.property_type,
                          message: errors.property_type,
                        }}
                        required={true}
                        isDisabled={location?.state?.isLand || isEdit}
                        isClearable={!location?.state?.isLand && !isEdit}
                        className="g-col-6 g-col-xxl-4 mb-3"
                      />

                      <CustomSelect
                        label={{ name: "Transaction Type" }}
                        name="transaction_type"
                        value={values.transaction_type}
                        onChange={(opt) => {
                          const val = opt?.value || "";
                          setValues({
                            ...values,
                            transaction_type: val,
                            rent_earning: val === "Rent" ? "Yes" : "",
                          });
                        }}
                        options={(propertyConfig?.transaction_types || []).map((tt) => ({ value: tt, label: tt }))}
                        placeholder="Select Transaction Type"
                        validation={{
                          isInvalid: touched.transaction_type && errors.transaction_type,
                          message: errors.transaction_type,
                        }}
                        required={true}
                        className="g-col-6 g-col-xxl-4 mb-3"
                      />
                    </div>
                  </Card>

                  {/* Location Card */}
                  <Card className="p-3 mb-4 border bg-light bg-opacity-10">
                    <h6 className="mb-3 text-muted">Location Details</h6>
                    <div className="grid gap-3">
                      <CustomSelect
                        label={{ name: "State" }}
                        required
                        className="g-col-6 g-col-xxl-4 mb-3"
                        inputId="state_id"
                        options={stateOptions}
                        value={stateOptions.find(o => o.value === values.state_id) || null}
                        onChange={(opt) => {
                          handleStateChange(opt, values, setValues, values.property_type);
                          setSelectedOptions(prev => ({
                            ...prev,
                            state: opt,
                            city: null,
                            locality: null,
                            district: null,
                            taluka: null,
                            village: null,
                          }));
                        }}
                        onInputChange={handleStateSearch}
                        onMenuOpen={() => {
                          dispatch(fetchActiveStates({ limit: 50 }));
                        }}
                        placeholder="Select State"
                        isClearable
                        filterOption={() => true}
                        validation={{
                          isInvalid: touched.state_id && errors.state_id,
                          message: errors.state_id,
                        }}
                      />

                      {values.property_type && (
                        values.property_type === "Land" ? (
                          <>
                            <CustomSelect
                              label={{ name: "District" }}
                              required
                              className="g-col-6 g-col-xxl-4 mb-3"
                              inputId="district_id"
                              options={districtOptions}
                              value={districtOptions.find(o => o.value === values.district_id) || null}
                              onChange={(opt) => {
                                handleDistrictChange(opt, values, setValues);
                                setSelectedOptions(prev => ({
                                  ...prev,
                                  district: opt,
                                  taluka: null,
                                  village: null,
                                }));
                              }}
                              onInputChange={handleDistrictSearch}
                              onMenuOpen={() => {
                                if (values.state_id) {
                                  dispatch(fetchActiveDistricts({ limit: 50, state_ids: values.state_id }));
                                }
                              }}
                              placeholder="Select District"
                              isClearable
                              isDisabled={!values.state_id}
                            
                              filterOption={() => true}
                              selectProps={{ styles: { menuPortal: base => ({ ...base, zIndex: 9999 }) } }}
                              validation={{
                                isInvalid: touched.district_id && errors.district_id,
                                message: errors.district_id,
                              }}
                            />
                            <CustomSelect
                              label={{ name: "Taluka" }}
                              required
                              className="g-col-6 g-col-xxl-4 mb-3"
                              inputId="taluka_id"
                              options={talukaOptions}
                              value={talukaOptions.find(o => o.value === values.taluka_id) || null}
                              onChange={(opt) => {
                                handleTalukaChange(opt, values, setValues);
                                setSelectedOptions(prev => ({
                                  ...prev,
                                  taluka: opt,
                                  village: null,
                                }));
                              }}
                              onInputChange={handleTalukaSearch}
                              onMenuOpen={() => {
                                if (values.district_id) {
                                  dispatch(fetchActiveTalukas({ limit: 50, district_ids: values.district_id }));
                                }
                              }}
                              placeholder="Select Taluka"
                              isClearable
                              isDisabled={!values.district_id}
                              
                              filterOption={() => true}
                              validation={{
                                isInvalid: touched.taluka_id && errors.taluka_id,
                                message: errors.taluka_id,
                              }}
                            />
                            <CustomSelect
                              label={{ name: "Village" }}
                              className="g-col-6 g-col-xxl-4 mb-3"
                              inputId="village_id"
                              options={villageOptions}
                              value={villageOptions.find(o => o.value === values.village_id) || null}
                              onChange={(opt) => {
                                setFieldValue("village_id", opt?.value || "");
                                setSelectedOptions(prev => ({ ...prev, village: opt }));
                              }}
                              onInputChange={handleVillageSearch}
                              onMenuOpen={() => {
                                if (values.taluka_id) {
                                  dispatch(fetchActiveVillages({ limit: 50, taluka_ids: values.taluka_id }));
                                }
                              }}
                              placeholder="Select Village"
                              isClearable
                              isDisabled={!values.taluka_id}
                              filterOption={() => true}
                              validation={{
                                isInvalid: touched.village_id && errors.village_id,
                                message: errors.village_id,
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <CustomSelect
                              label={{ name: "City" }}
                              required
                              className="g-col-6 g-col-xxl-4 mb-3"
                              inputId="city_id"
                              options={cityOptions}
                              value={cityOptions.find(o => o.value === values.city_id) || null}
                              onChange={(opt) => {
                                handleCityChange(opt, values, setValues);
                                setSelectedOptions(prev => ({
                                  ...prev,
                                  city: opt,
                                  locality: null,
                                }));
                              }}
                              onInputChange={handleCitySearch}
                              onMenuOpen={() => {
                                if (values.state_id) {
                                  dispatch(fetchActiveCities({ limit: 50, state_ids: values.state_id }));
                                }
                              }}
                              placeholder="Select City"
                              isClearable
                              isDisabled={!values.state_id}
                              
                              filterOption={() => true}
                              
                              validation={{
                                isInvalid: touched.city_id && errors.city_id,
                                message: errors.city_id,
                              }}
                            />
                            <CustomSelect
                              label={{ name: "Locality" }}
                              className="g-col-6 g-col-xxl-4 mb-3"
                              inputId="locality_id"
                              options={localityOptions}
                              value={localityOptions.find(o => o.value === values.locality_id) || null}
                              onChange={(opt) => {
                                setFieldValue("locality_id", opt?.value || "");
                                setSelectedOptions(prev => ({ ...prev, locality: opt }));
                              }}
                              onInputChange={handleLocalitySearch}
                              onMenuOpen={() => {
                                if (values.city_id) {
                                  dispatch(fetchActiveLocalities({ limit: 50, city_ids: values.city_id }));
                                }
                              }}
                              placeholder="Select Locality"
                              isClearable
                              isDisabled={!values.city_id}
                             
                              filterOption={() => true}
                             
                              validation={{
                                isInvalid: touched.locality_id && errors.locality_id,
                                message: errors.locality_id,
                              }}
                            />
                          </>
                        )
                      )}
                    </div>
                  </Card>

                  {/* Specific Details Card */}
                  {values.property_type && (
                    <Card className="p-3 mb-4 border bg-light bg-opacity-10">
                      <h6 className="mb-3 text-muted">
                        {values.property_type === "Land" ? "Land Specifics" : "Property Details"}
                      </h6>
                      <div className="grid gap-3">
                        {values.property_type === "Land" ? (
                          <>
                            {renderDropdown(
                              "land_type",
                              "Land Type",
                              (propertyConfig?.land_types || []).map((t) => ({ value: t, label: t })),
                              true
                            )}
                            {renderDropdown(
                              "tenure",
                              "Tenure",
                              (propertyConfig?.tenure || []).map((t) => ({ value: t, label: t })),
                              true
                            )}
                            {renderAreaField("land_area", "Land Area", "land_area_unit", true)}
                            {renderInput("survey_no", "Survey No")}
                            {renderInput("fp_no", "FP No")}
                            {renderInput("tp_no", "TP No")}
                            {renderDropdown(
                              "zone",
                              "Zone",
                              (propertyConfig?.zones || []).map((z) => ({ value: z, label: z }))
                            )}
                            {renderDropdown(
                              "road_type",
                              "Road Type",
                              (propertyConfig?.road_types || []).map((r) => ({ value: r, label: r }))
                            )}
                            {renderInput("road_size", "Road Size")}
                            {renderInput("jantri_rate", "Jantri Rate")}
                            {renderInput("dastaveg_value", "Dastaveg Value")}
                            {renderDropdown(
                              "tag",
                              "Tag",
                              (propertyConfig?.tags || []).map((t) => ({ value: t, label: t }))
                            )}

                            {renderPriceField(
                              "price_per_unit",
                              "Price Per Unit",
                              "price_unit",
                              true
                            )}
                            {renderInput(
                              "total_price",
                              "Total Price",
                              "text",
                              "",
                              true,
                              true
                            )}
                            {renderInput("map_location.address", "Map Address")}
                            {renderMapLocationField()}
                          </>
                        ) : (
                          <>
                            {["Flat", "Penthouse", "Serviced Apartment", "Row House"].includes(values.property_type) &&
                              renderDropdown(
                                "bhk",
                                "BHK",
                                (propertyConfig?.bhk || []).map((b) => ({ value: b, label: b })),
                                true
                              )
                            }
                            {renderAreaField("carpet_area", "Carpet Area", "carpet_area_unit")}
                            {renderAreaField(
                              "s_builtup_area",
                              "Super Builtup Area",
                              "s_builtup_area_unit",
                              values.property_type && values.property_type !== "Land"
                            )}
                            {renderAreaField(
                              "plot_area",
                              "Plot Area",
                              "plot_area_unit",
                              values.property_type && [
                                "Row House",
                                "Residential Plot",
                                "Industrial Plot",
                                "Industrial Shed/ Building",
                              ].includes(values.property_type)
                            )}
                            {renderPriceField(
                              "price_per_unit",
                              "Price Per Unit",
                              "price_unit",
                              values.property_type && values.property_type !== "Land"
                            )}
                            {renderInput(
                              "total_price",
                              "Total Price",
                              "text",
                              "",
                              values.property_type && values.property_type !== "Land",
                              true
                            )}
                            {renderDropdown(
                              "possession",
                              "Possession",
                              (propertyConfig?.possessions || []).map((p) => ({ value: p, label: p })),
                              values.property_type && values.property_type !== "Land"
                            )}
                            {!["Residential Plot", "Industrial Plot", "Industrial Shed/ Building"].includes(values.property_type) &&
                              renderDropdown(
                                "furnishing_status",
                                "Furnishing Status",
                                (propertyConfig?.furnishing_statuses || []).map((f) => ({ value: f, label: f })),
                                true
                              )
                            }
                            {renderInput("society_project_name", "Society/Project Name")}
                            {renderInput("developer_name", "Developer Name")}
                            {renderInput("map_location.address", "Map Address")}
                            {renderMapLocationField()}
                          </>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Property Specifications Card */}
                  {values.property_type && values.property_type !== "Land" && [
                    "floor_no",
                    "flat_per_floor",
                    "total_floor",
                    "no_of_wings",
                    "bath",
                    "lift",
                    "parking_car",
                    "parking_2w"
                  ].some(field => !!propertyConfig?.fields?.[values.property_type]?.[field]) && (
                      <Card className="p-3 mb-4 border bg-light bg-opacity-10">
                        <h6 className="mb-3 text-muted">Property Specifications</h6>
                        <div className="grid gap-3">
                          {!!propertyConfig?.fields?.[values.property_type]?.bath && renderDropdown(
                            "bath",
                            "Baths",
                            (propertyConfig?.baths || []).map((b) => ({ value: b, label: b }))
                          )}
                          {!!propertyConfig?.fields?.[values.property_type]?.lift && renderDropdown(
                            "lift",
                            "Lifts",
                            (propertyConfig?.lifts || []).map((l) => ({ value: l, label: l }))
                          )}
                          {!!propertyConfig?.fields?.[values.property_type]?.parking_car && renderDropdown(
                            "parking_car",
                            "Car Parking",
                            (propertyConfig?.parking_car || []).map((p) => ({ value: p, label: p }))
                          )}
                          {!!propertyConfig?.fields?.[values.property_type]?.parking_2w && renderDropdown(
                            "parking_2w",
                            "2 Wheeler Parking",
                            (propertyConfig?.parking_2w || []).map((p) => ({ value: p, label: p }))
                          )}
                          {!!propertyConfig?.fields?.[values.property_type]?.floor_no && renderDropdown(
                            "floor_no",
                            "Floor No",
                            (propertyConfig?.floor_no || []).map((f) => ({ value: f, label: f }))
                          )}
                          {!!propertyConfig?.fields?.[values.property_type]?.flat_per_floor && renderDropdown(
                            "flat_per_floor",
                            "Flats Per Floor",
                            (propertyConfig?.flat_per_floor || []).map((f) => ({ value: f, label: f }))
                          )}
                          {!!propertyConfig?.fields?.[values.property_type]?.total_floor && renderDropdown(
                            "total_floor",
                            "Total Floors",
                            (propertyConfig?.total_floor || []).map((f) => ({ value: f, label: f }))
                          )}
                          {!!propertyConfig?.fields?.[values.property_type]?.no_of_wings && renderDropdown(
                            "no_of_wings",
                            "No of Wings",
                            (propertyConfig?.no_of_wings || []).map((w) => ({ value: w, label: w }))
                          )}
                        </div>
                      </Card>
                    )}

                  {/* File Uploads Card */}
                  <Card className="p-3 mb-4 border bg-light bg-opacity-10">
                    <h6 className="mb-3 text-muted">Media Files</h6>
                    <div>
                      <div className="mb-4">
                        <FormLabel labelName="Photos (Maximum 5)" />
                        <div className="d-flex flex-wrap gap-3 mb-2">
                          {(values.photos || []).map((file, index) => (
                            <div
                              key={file.id || index}
                              className="position-relative border rounded p-1"
                              style={{ width: "100px", height: "100px" }}
                            >
                              <img
                                src={file.url}
                                alt={`Property ${index + 1}`}
                                style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                                className="rounded"
                                onClick={() => setPreviewMedia({ type: "image", url: file.url, title: `Photo ${index + 1}` })}
                              />
                              <button
                                type="button"
                                className="position-absolute top-0 end-0 bg-danger text-white border-0 rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  marginTop: "-5px",
                                  marginRight: "-5px",
                                  fontSize: "12px",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleFileDelete(file.id, "photos", values, setFieldValue)}
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                          {(!values.photos || values.photos.length < 5) && (
                            <label
                              className="d-flex flex-column align-items-center justify-content-center border border-dashed rounded bg-light"
                              style={{ width: "100px", height: "100px", cursor: "pointer", borderStyle: "dashed" }}
                            >
                              <span className="text-muted fs-4">+</span>
                              <span className="text-muted" style={{ fontSize: "10px" }}>
                                Upload
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                className="d-none"
                                disabled={!values.user_id}
                                onChange={(e) => handleFileUpload(e, "photos", values, setFieldValue)}
                              />
                            </label>
                          )}
                        </div>
                        {!values.user_id && (
                          <small className="text-warning">Please select a broker first to enable photo uploads.</small>
                        )}
                      </div>

                      <div className="mb-4">
                        <FormLabel labelName="Video (Maximum 1)" />
                        <div className="d-flex flex-wrap gap-3 mb-2">
                          {values.video ? (
                            <div className="d-flex flex-column align-items-start gap-2">
                              <div
                                className="rounded-2 border d-flex align-items-center justify-content-center position-relative overflow-hidden bg-dark"
                                style={{ width: "160px", height: "100px", cursor: "pointer" }}
                                onClick={() => setPreviewMedia({ type: "video", url: values.video.url, title: "Property Video" })}
                              >
                                <div className="text-center position-relative">
                                  <span className="d-block small fw-bold text-white">▶ Play Video</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => handleFileDelete(values.video.id, "video", values, setFieldValue)}
                              >
                                Delete
                              </button>
                            </div>
                          ) : (
                            <label
                              className="d-flex flex-column align-items-center justify-content-center border border-dashed rounded bg-light"
                              style={{ width: "120px", height: "50px", cursor: "pointer", borderStyle: "dashed" }}
                            >
                              <span className="text-muted" style={{ fontSize: "12px" }}>
                                Upload Video
                              </span>
                              <input
                                type="file"
                                accept="video/*"
                                className="d-none"
                                disabled={!values.user_id}
                                onChange={(e) => handleFileUpload(e, "video", values, setFieldValue)}
                              />
                            </label>
                          )}
                        </div>
                        {!values.user_id && (
                          <small className="text-warning">Please select a broker first to enable video upload.</small>
                        )}
                      </div>

                      <div className="mb-4">
                        <FormLabel labelName="Brochure Attachment (PDF)" />
                        <div className="d-flex flex-wrap gap-3 mb-2">
                          {values.brochure_attachment ? (
                            <div className="d-flex flex-column align-items-start gap-2">
                              <div
                                className="rounded-2 border d-flex align-items-center justify-content-center bg-light"
                                style={{ width: "120px", height: "60px", cursor: "pointer" }}
                                onClick={() => setPreviewMedia({ type: "document", url: values.brochure_attachment.url, title: "Brochure" })}
                              >
                                <span className="small fw-bold text-success">📄 View PDF</span>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() =>
                                  handleFileDelete(
                                    values.brochure_attachment.id,
                                    "brochure_attachment",
                                    values,
                                    setFieldValue
                                  )
                                }
                              >
                                Delete
                              </button>
                            </div>
                          ) : (
                            <label
                              className="d-flex flex-column align-items-center justify-content-center border border-dashed rounded bg-light"
                              style={{ width: "120px", height: "50px", cursor: "pointer", borderStyle: "dashed" }}
                            >
                              <span className="text-muted" style={{ fontSize: "12px" }}>
                                Upload PDF
                              </span>
                              <input
                                type="file"
                                accept=".pdf"
                                className="d-none"
                                disabled={!values.user_id}
                                onChange={(e) => handleFileUpload(e, "brochure_attachment", values, setFieldValue)}
                              />
                            </label>
                          )}
                        </div>
                        {!values.user_id && (
                          <small className="text-warning">Please select a broker first to enable brochure upload.</small>
                        )}
                      </div>

                      {/* Media Preview Modal */}
                      <PreviewModal
                        show={!!previewMedia}
                        onHide={() => setPreviewMedia(null)}
                        previewMedia={previewMedia ? [previewMedia] : []}
                      />

                      {values.property_type !== "Land" && (
                        <div className="mb-4">
                          <FormLabel labelName="Amenities" />
                          <div className="d-flex flex-wrap gap-2 mt-2">
                            {(propertyConfig?.amenities || []).map((amenity) => {
                              const isSelected = (values.amenities || []).includes(amenity);
                              return (
                                <button
                                  key={amenity}
                                  type="button"
                                  className={`btn btn-sm rounded-pill border transition-all ${isSelected
                                    ? "btn-primary border-primary"
                                    : "btn-outline-secondary border-secondary text-muted"
                                    }`}
                                  style={{ fontSize: "12px" }}
                                  onClick={() => handleAmenityToggle(amenity, values, setFieldValue)}
                                >
                                  {amenity}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {renderTextArea("notes", "Notes")}

                      {!isPublicProperty && (
                        <div className="mt-3">
                          <Form.Check
                            type="checkbox"
                            id="is_public"
                            label="Make this property public"
                            checked={values.is_public}
                            onChange={(e) => setFieldValue("is_public", e.target.checked)}
                          />
                        </div>
                      )}
                    </div>
                  </Card>

                  {/* Rent Earning Details Card */}
                  {values.transaction_type && (
                    <Card className="p-3 mb-4 border bg-light bg-opacity-10">
                      <h6 className="mb-3 text-muted">Rent Earning Details</h6>
                      <div className="grid gap-3">
                        {values.transaction_type === "Sell" && (
                          <CustomSelect
                            label={{ name: "Is Property Rent Earning?" }}
                            name="rent_earning"
                            value={values.rent_earning}
                            onChange={(opt) => setFieldValue("rent_earning", opt ? opt.value : "")}
                            options={[
                              { value: "Yes", label: "Yes" },
                              { value: "No", label: "No" },
                            ]}
                            placeholder="Select Yes/No"
                            isClearable={true}
                            className="g-col-6 g-col-xxl-4 mb-3"
                          />
                        )}

                        {(values.transaction_type === "Rent" || values.rent_earning === "Yes" || values.rent_earning === true) && (
                          <>
                            {renderDropdown(
                              "tenant_type",
                              "Tenant Type",
                              (propertyConfig?.tenant_type || []).map((t) => ({ value: t, label: t }))
                            )}
                            {renderInput("rent_per_month", "Rent Per Month")}
                            {renderInput("rent_per_unit", "Rent Per Unit")}
                            {renderInput("deposit", "Deposit")}
                            {renderDropdown(
                              "contract_tenure",
                              "Contract Tenure",
                              (propertyConfig?.contract_tenure || []).map((c) => ({ value: c, label: c }))
                            )}
                            {renderDropdown(
                              "contract_duration",
                              "Contract Duration",
                              (propertyConfig?.contract_duration || []).map((c) => ({ value: c, label: c }))
                            )}
                            {renderDropdown(
                              "rent_increment",
                              "Rent Increment",
                              (propertyConfig?.rent_increment || []).map((r) => ({ value: r, label: r }))
                            )}
                            {renderDropdown(
                              "rent_increment_tenure",
                              "Rent Increment Tenure",
                              (propertyConfig?.rent_increment_tenure || []).map((r) => ({ value: r, label: r }))
                            )}
                            {renderDropdown(
                              "lock_in",
                              "Lock-In Period",
                              (propertyConfig?.lock_in || []).map((l) => ({ value: l, label: l }))
                            )}
                            {renderDropdown(
                              "notice_period",
                              "Notice Period",
                              (propertyConfig?.notice_period || []).map((n) => ({ value: n, label: n }))
                            )}
                            {renderInput("maintenance_amount_per_month", "Maintenance Amount Per Month")}
                            {renderInput("municipal_tax_annually", "Municipal Tax Annually")}
                          </>
                        )}
                      </div>
                    </Card>
                  )}

                </div>

                {/* Submission Section */}
                <div className="d-flex justify-content-end gap-3 mt-4">
                  <CommonButton
                    type="button"
                    variant="outline"
                    title="Cancel"
                    onClick={() => navigate(backUrl)}
                  />
                  <CommonButton
                    type="submit"
                    variant="primary"
                    title={
                      isEdit
                        ? (isPublicProperty ? (propertyType === "Land" ? "Update Public Land" : "Update Public Property") : (propertyType === "Land" ? "Update Land" : "Update Property"))
                        : (isPublicProperty ? (propertyType === "Land" ? "Create Public Land" : "Create Public Property") : (propertyType === "Land" ? "Create Land" : "Create Property"))
                    }
                  />
                </div>

                {/* Map Picker Modal */}
                <MapPickerModal
                  isOpen={showMapModal}
                  onClose={() => setShowMapModal(false)}
                  title="Select Location on Map"
                  size="lg"
                  latitude={values.map_location?.latitude}
                  longitude={values.map_location?.longitude}
                  onSave={(lat, lng, address) => {
                    setFieldValue("map_location.latitude", lat);
                    setFieldValue("map_location.longitude", lng);
                    if (address) {
                      setFieldValue("map_location.address", address);
                    }
                  }}
                />

              </Card>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default AddBrokerProperty;
